'use client';

import { c as _c } from "react/compiler-runtime";
import { loginRedirect, type Role } from '@svs/nova-core/nextjs/auth';
import { getFormattedCurrency } from '@svs/nova-poolbetting/core/lib/client';
import { useCouponWallets } from '@svs/nova-poolbetting/core/nextjs/coupon';
import { useBalance } from '@svs/nova-poolbetting/core/nextjs/lib/client';
import { type Coupon, getCouponId, getCouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { ExternalWagerSelectedSystemType } from '@svs/nova-poolbetting/schema/wager';
import type { ReactNode } from 'react';
import BetslipExternal from './BetslipExternal';
type BetslipExternalClientContainerProps = {
  betslipContent: ReactNode;
  amount: number;
  numberOfRows: number;
  product: Definition['drawProducts'][number];
  couponType: Coupon['couponType'];
  selectedSystem: Coupon['selectedSystem'];
  drawNumber: DrawNumber;
  role: Role;
};
function isExternalWagerSelectedSystemType(system: Coupon['selectedSystem']): system is ExternalWagerSelectedSystemType {
  return system === 'MSYS' || system === 'SROW';
}

/**
 * Component för external betslip.
 *
 * @param props - The props object.
 * @param props.betslipContent - The overview component to be displayed in the drawer
 * @param props.amount - The amount for the bet
 * @param props.numberOfRows - The number of rows/systems in the betslip
 * @param props.product - The product associated with the betslip
 * @param props.couponType - The type of the coupon for the betslip
 * @param props.selectedSystem - The selected system for the betslip
 * @param props.drawNumber - The draw number associated with the betslip
 * @param props.role - The role of the user (e.g., 'Retailer', 'Player')
 * @returns external betslip component
 * @throws {Error} if coupon selected system is not supported for external wagers
 */
function BetslipExternalClientContainer(t0) {
  const $ = _c(20);
  const {
    product,
    drawNumber,
    couponType,
    selectedSystem,
    betslipContent,
    amount,
    numberOfRows,
    role
  } = t0;
  let t1;
  if ($[0] !== couponType || $[1] !== drawNumber || $[2] !== product) {
    t1 = getCouponId(product, drawNumber, couponType);
    $[0] = couponType;
    $[1] = drawNumber;
    $[2] = product;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const couponId = t1;
  if (!getCouponIndex(0)) {
    throw new Error("Invalid coupon index for external coupon");
  }
  let t2;
  if ($[4] !== couponId) {
    t2 = {
      couponId
    };
    $[4] = couponId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const activeWallets = useCouponWallets(t2);
  let t3;
  if ($[6] !== activeWallets || $[7] !== drawNumber || $[8] !== product || $[9] !== role) {
    t3 = {
      activeWallets,
      couponIndex: 0,
      drawNumber,
      product,
      role
    };
    $[6] = activeWallets;
    $[7] = drawNumber;
    $[8] = product;
    $[9] = role;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    balance,
    shouldRedirectUser
  } = useBalance(t3);
  if (!isExternalWagerSelectedSystemType(selectedSystem)) {
    throw new Error(`Unsupported external selected system: ${selectedSystem}`);
  }
  const handleBalanceClick = shouldRedirectUser ? _temp : undefined;
  let t4;
  if ($[11] !== amount) {
    t4 = getFormattedCurrency(amount, {
      unit: "KR"
    });
    $[11] = amount;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== balance || $[14] !== betslipContent || $[15] !== handleBalanceClick || $[16] !== numberOfRows || $[17] !== selectedSystem || $[18] !== t4) {
    const betslipProps = {
      price: t4,
      selectedSystem,
      betslipContent,
      balance,
      handleBalanceClick,
      numberOfRows
    };
    t5 = <BetslipExternal betslipProps={betslipProps} />;
    $[13] = balance;
    $[14] = betslipContent;
    $[15] = handleBalanceClick;
    $[16] = numberOfRows;
    $[17] = selectedSystem;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function _temp() {
  loginRedirect();
}
export default BetslipExternalClientContainer;